import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { MapsAPILoader } from '@ng-maps/core';
import { v4 as uuidv4 } from 'uuid';

import { MapPoint } from '../../models/map-point.model';

/**
 * Mapa interactivo genérico sobre Google Maps, con clustering. A diferencia de `gmap-view`
 * (acoplado a `Order` y con contenedor fijo), usa un `id` por instancia y redibuja los markers
 * en `ngOnChanges`. El click sobre un marker sólo emite `select`: el detalle lo resuelve el padre.
 */
@Component({
  selector: 'silobag-map',
  templateUrl: './silobag-map.component.html',
  styleUrls: ['./silobag-map.component.scss']
})
export class SilobagMapComponent implements AfterViewInit, OnChanges, OnDestroy {

  @Input() public points: MapPoint[] = [];
  @Input() public height = '480px';
  /** Centro por defecto cuando no hay puntos con coordenadas válidas (Argentina). */
  @Input() public defaultCenter: { lat: number, lng: number } = { lat: -34.0, lng: -63.0 };
  @Input() public defaultZoom = 4;

  @Output('select') readonly onSelect = new EventEmitter<string>();

  public readonly uuid: string = 'silobag-map-' + uuidv4();

  private map: google.maps.Map;
  private markers: google.maps.marker.AdvancedMarkerElement[] = [];
  private markersById: Map<string, google.maps.marker.AdvancedMarkerElement> = new Map();
  private clusterer: MarkerClusterer | null = null;
  /** Listener one-shot del acercamiento con un único punto; se cancela en cada re-render. */
  private zoomListener: google.maps.MapsEventListener | null = null;
  private ready = false;

  private static readonly PIN_COLORS: Record<string, { background: string, border: string, glyphColor: string }> = {
    alert:     { background: '#e6483d', border: '#a6291f', glyphColor: '#ffffff' },
    committed: { background: '#f2a83b', border: '#b2760f', glyphColor: '#ffffff' },
    available: { background: '#3fa876', border: '#256b4a', glyphColor: '#ffffff' },
    muted:     { background: '#9aa1ab', border: '#6b727b', glyphColor: '#ffffff' }
  };

  constructor(
    private mapsAPILoader: MapsAPILoader,
    private zone: NgZone
  ) { }

  ngAfterViewInit(): void {
    this.mapsAPILoader.load().then(() => {
      this.map = new google.maps.Map(document.getElementById(this.uuid), {
        fullscreenControl: true,
        mapTypeControl: true,
        streetViewControl: false,
        clickableIcons: false,
        maxZoom: 17,
        minZoom: 3,
        mapId: this.uuid,
        center: this.defaultCenter,
        zoom: this.defaultZoom
      });

      this.ready = true;
      this.render();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['points'] && this.ready) {
      this.render();
    }
  }

  ngOnDestroy(): void {
    this.clearMarkers();
  }

  private get validPoints(): MapPoint[] {
    return (this.points || []).filter(p =>
      typeof p.lat === 'number' && typeof p.lng === 'number' &&
      Number.isFinite(p.lat) && Number.isFinite(p.lng) &&
      !(p.lat === 0 && p.lng === 0)
    );
  }

  private render(): void {
    this.clearMarkers();

    const points = this.validPoints;
    if (!points.length) {
      this.map.setCenter(this.defaultCenter);
      this.map.setZoom(this.defaultZoom);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    // Jitter determinístico para no superponer marcadores que comparten coordenada exacta.
    const seen: Record<string, number> = {};

    points.forEach(point => {
      const key = point.lat + ',' + point.lng;
      const offset = seen[key] ?? 0;
      seen[key] = offset + 1;

      const position = {
        lat: point.lat + offset * 0.0002,
        lng: point.lng + offset * 0.0002
      };

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        map: this.map,
        content: this.buildPin(point).element,
        title: point.title
      });

      // Los listeners de Google Maps corren fuera de la zona de Angular: sin `zone.run` la vista no se refresca.
      marker.addListener('click', () => {
        this.zone.run(() => this.onSelect.emit(point.id));
      });

      bounds.extend(position);
      this.markers.push(marker);
      this.markersById.set(point.id, marker);
    });

    this.clusterer = new MarkerClusterer({ map: this.map, markers: this.markers });

    this.map.setCenter(bounds.getCenter());
    this.map.fitBounds(bounds);
    if (points.length === 1) {
      // Con un único punto, `bounds` tiene área cero: fitBounds no da un zoom útil por sí
      // solo, así que forzamos un acercamiento razonable una vez que el mapa termina de ajustarse.
      this.zoomListener = google.maps.event.addListenerOnce(this.map, 'idle', () => {
        this.zoomListener = null;
        this.map.setZoom(15);
      });
    }
  }

  private clearMarkers(): void {
    // `clearMarkers()` sólo vacía el clusterer: hace falta el `setMap(null)` para
    // desmontarlo del mapa junto con su listener de `idle`.
    this.clusterer?.clearMarkers();
    this.clusterer?.setMap(null);
    this.clusterer = null;

    if (this.zoomListener) {
      google.maps.event.removeListener(this.zoomListener);
      this.zoomListener = null;
    }

    this.markers.forEach(marker => marker.map = null);
    this.markers = [];
    this.markersById.clear();
  }

  /** Centra el mapa en el punto indicado, sin emitir `select`. */
  public focusPoint(id: string): void {
    const marker = this.markersById.get(id);
    if (!this.ready || !marker) return;

    this.map.panTo(marker.position as google.maps.LatLngLiteral);
    if ((this.map.getZoom() ?? 0) < 15) this.map.setZoom(15);
  }

  private buildPin(point: MapPoint): google.maps.marker.PinElement {
    const colors = SilobagMapComponent.PIN_COLORS[point.variant || 'available'];
    return new google.maps.marker.PinElement({
      background: colors.background,
      borderColor: colors.border,
      glyphColor: colors.glyphColor
    });
  }
}
